import { ApiError } from '@/lib/api/client';
import { sentenceCase } from '@/lib/contract-labels';

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * The words a form shows for a failed request (C2-31): the server's
 * message with every field it names rewritten as the field's label
 * ("waiver_reason" → "Waiver reason") and any id replaced by "this item".
 * `labels` maps the API's field keys to the labels the form uses; keys the
 * caller does not map read as plain words.
 */
export function describeApiError(
  err: unknown,
  labels: Record<string, string> = {},
  fallback = 'Something went wrong. Try again.',
): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error && err.message ? err.message : fallback;
  }
  const details = err.details ?? {};
  const fields = Object.keys(details).filter((k) => k !== 'code');
  let text = err.message ?? '';
  for (const key of fields) {
    const label = fieldLabel(key, labels);
    // A snake_case key is an identifier wherever it appears. A one-word key
    // ("reason", "title") is also plain English, so it is the field only
    // where the server's message opens with it.
    const identifier = key.includes('_');
    text = text.replace(new RegExp(`\\b${escapeRegExp(key)}\\b`, 'g'), (match, offset: number) =>
      offset === 0 ? label : identifier ? midSentence(label) : match,
    );
  }
  text = text.replace(UUID_RE, 'this item').trim();
  if (text.length > 0) return text.charAt(0).toUpperCase() + text.slice(1);
  if (fields.length > 0) {
    return fields.map((k) => `${fieldLabel(k, labels)} ${describeValue(details[k])}`).join(' ');
  }
  return fallback;
}

/**
 * For an inline <ErrorMessage>: a validation error becomes its labelled
 * words; any other error is passed through so the code and correlation id
 * still render for support.
 */
export function labelledError(err: unknown, labels: Record<string, string> = {}): unknown {
  if (err instanceof ApiError && err.code === 'validation') return describeApiError(err, labels);
  return err;
}

function fieldLabel(key: string, labels: Record<string, string>): string {
  return labels[key] ?? sentenceCase(key);
}

/** "Effective date" → "effective date"; an acronym ("SOW reference", "AI run") keeps its case. */
function midSentence(label: string): string {
  const first = label.split(' ')[0] ?? '';
  if (first.length > 1 && first === first.toUpperCase()) return label;
  return label.charAt(0).toLowerCase() + label.slice(1);
}

function describeValue(value: unknown): string {
  switch (value) {
    case 'required':
      return 'is required.';
    case 'too_short':
      return 'is too short.';
    case 'invalid':
    case 'enum':
      return 'is not valid.';
    default:
      return typeof value === 'string' ? `${value}.` : 'is not valid.';
  }
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
